/** Explicit IDs for account, membership and football profile operations.
 * A row key, contact email or synthetic profile-* ID never identifies a user.
 */
const idValue = (value) => typeof value === 'string' && value.trim() ? value.trim() : null;

export function getPlayerProfileId(row) {
  return idValue(row?.player_profile_id) || idValue(row?.profile_id);
}

// Only call with a row returned by the roster endpoint (or a normalized profile).
export function getRosterProfileId(player) {
  return getPlayerProfileId(player)
    || (player?.source !== 'membership' ? idValue(player?.id) : null);
}

export function getMemberUserId(row) {
  const profileIds = [getPlayerProfileId(row), idValue(row?.profile_id)];
  if (row?.source === 'roster_profile') profileIds.push(idValue(row.id));
  for (const value of [row?.linked_user_id, row?.user_id]) {
    const id = idValue(value);
    if (id && !id.startsWith('profile-') && !profileIds.includes(id)) return id;
  }
  return null;
}

export function getMembershipId(row) {
  const id = idValue(row?.membership_id);
  if (!id || id.startsWith('profile-') || id === getPlayerProfileId(row)
      || id === getMemberUserId(row)) return null;
  return id;
}

/** Match a roster row by profile first, then by its explicit linked account.
 * Email may be a guardian's inbox shared by siblings; it is never a join key.
 */
export function resolveRosterMember(player, members = []) {
  if (!player) return null;
  const profileId = getRosterProfileId(player);
  const linkedId = getMemberUserId({ ...player, player_profile_id: profileId });
  const rows = Array.isArray(members) ? members : [];
  const profileMember = profileId ? rows.find((row) =>
    [getPlayerProfileId(row), idValue(row.profile_id)].includes(profileId)
      && (!linkedId || !getMemberUserId(row) || getMemberUserId(row) === linkedId)
  ) : null;
  const accountMember = linkedId ? rows.find((row) =>
    getMemberUserId(row) === linkedId && row.role === 'player'
  ) || rows.find((row) => getMemberUserId(row) === linkedId) : null;
  const match = profileMember || accountMember;
  const userId = linkedId || getMemberUserId(match);
  const playerMatch = match?.role === 'player';
  return {
    ...(match || {}),
    id: match?.id || (profileId ? `profile-${profileId}` : null),
    user_id: userId,
    linked_user_id: userId,
    player_profile_id: profileId,
    membership_id: playerMatch ? getMembershipId(match) : null,
    source: match?.source || 'roster_profile',
    name: player.display_name || player.name || match?.name || '',
    email: match ? (match.email || '') : (userId ? '' : (player.child_email || player.email || '')),
    role: 'player',
    account_state: (playerMatch && match.account_state) || player.account_state
      || (userId ? 'ACTIVE' : 'NO_ACCOUNT'),
    activation: (playerMatch && match.activation) || player.activation || null,
    account_status: match?.account_status || (userId ? 'active' : 'not_invited'),
    pending_invitation: (playerMatch && match.pending_invitation) || null,
  };
}
